/* eslint-disable react-hooks/exhaustive-deps */
import { get, ref, update } from "firebase/database";
import React, { useEffect, useState } from "react";
import { database, storage } from "../../../firebase";
import { toast } from "react-toastify";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

const AdminProfile = ({ user }) => {
  const [getCompany, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedUser, setUpdatedUser] = useState(null);
  const [updatedCompany, setUpdatedCompany] = useState(null);
  const [awards, setAwards] = useState([]); // State for multiple award images
  const [banner, setBanner] = useState(null); // State for banner image
  const [branch, setBranch] = useState(null); // State for banner image

  useEffect(() => {
    const userId = user?.uid;
    if (userId) {
      fetchData(userId);
    }
  }, [user]);

  // Upload file to Firebase Storage and get URL
  const uploadFile = async (file, folder) => {
    const fileRef = storageRef(storage, `${folder}/${file.name}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        null,
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const fetchData = async (userId) => {
    try {
      if (userId) {
        const userRef = ref(database, `users/${userId}`);
        const snapshot = await get(userRef);

        if (snapshot.exists()) {
          const userData = snapshot.val();
          setUpdatedUser(userData);

          const companyId = userData.companyId;
          if (companyId) {
            const companyRef = ref(database, `companies/${companyId}`);
            const companySnapshot = await get(companyRef);

            if (companySnapshot.exists()) {
              const companyData = companySnapshot.val();

              // Set company and emailSignature data properly
              setCompany(companyData);
              const emailSignature = companyData.emailSignature || {}; // Fallback to empty object if undefined
              setUpdatedCompany({
                ...companyData,
                emailSignature: {
                  awards: emailSignature.awards || [], // Initialize awards as an empty array if undefined
                  banner: emailSignature.banner || "", // Initialize banner as an empty string if undefined
                },
              });
              setAwards(emailSignature.awards || []); // Ensure awards is always an array
              setBanner(emailSignature.banner || ""); // Ensure banner is a string
              setBranch(companyData.branch || "");
            } else {
              throw new Error("Company data not found.");
            }
          }
        } else {
          throw new Error("User data not found.");
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Save updated user data
      if (updatedUser) {
        const userRef = ref(database, `users/${updatedUser.uid}`);
        await update(userRef, updatedUser);
      }
  
      // Save updated company data with emailSignature and branch
      if (updatedCompany) {
        const companyRef = ref(database, `companies/${updatedUser.companyId}`);
  
        // Ensure that branch is included in the updatedCompanyData
        const updatedCompanyData = {
          ...updatedCompany,
          emailSignature: {
            ...updatedCompany.emailSignature,
            awards, // Updating the awards here
            banner, // Updating the banner here
          },
          branch, // Ensure branch is updated here
        };
  
        await update(companyRef, updatedCompanyData);
      }
  
      toast.success("Update Successfully");
      window.location.reload();
    } catch (error) {
      toast.error("Failed to save changes.");
    }
  };
  

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadedImageUrl = await uploadFile(file, "company-logos");
      setUpdatedCompany({ ...updatedCompany, logo: uploadedImageUrl });
    }
  };

  const handleAwardsUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploadedAwardUrls = [];

    for (const file of files) {
      const uploadedUrl = await uploadFile(file, "awards");
      uploadedAwardUrls.push(uploadedUrl);
    }

    setAwards(uploadedAwardUrls);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadedBannerUrl = await uploadFile(file, "banners");
      setBanner(uploadedBannerUrl);
    }
  };

  if (loading) {
    return (
      <div className="text-center text-lg font-semibold py-10">Loading...</div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 font-semibold py-10">
        {error}
      </div>
    );
  }

  return (
    <div className="flex mx-4 justify-center items-start">
      <div className="bg-white p-8 w-full">
        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {getCompany && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Logo Section */}
              <div className="flex flex-col items-start">
                <label className="text-sm font-medium text-gray-700">
                  Company Logo
                </label>
                <div className="mt-2">
                  <img
                    src={updatedCompany?.logo || "/default-logo.png"}
                    alt="Company Logo"
                    className="w-full h-24 rounded-sm border-2 border-gray-300 object-contain mb-4 transition-all duration-300 ease-in-out transform hover:scale-105"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="bg-gray-100 text-gray-700 file:border file:border-gray-400 file:rounded-lg file:px-2 file:py-1 file:hover:bg-gray-200 file:hover:border-blue-500 transition-all"
                  />
                </div>
              </div>

              {/* Company Name Section */}
              <div className="flex flex-col items-start justify-end">
                <label className="text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  value={updatedCompany.companyName || ""}
                  onChange={(e) =>
                    setUpdatedCompany({
                      ...updatedCompany,
                      companyName: e.target.value,
                    })
                  }
                  className="w-full p-2 mt-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Address Section */}
              <div className="flex flex-col items-start">
                <label className="text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  type="text"
                  value={updatedCompany.address || ""}
                  onChange={(e) =>
                    setUpdatedCompany({
                      ...updatedCompany,
                      address: e.target.value,
                    })
                  }
                  className="w-full p-2 mt-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Google Review Link Section */}
              <div className="flex flex-col items-start">
                <label className="text-sm font-medium text-gray-700">
                  Google Review Link
                </label>
                <input
                  type="text"
                  value={updatedCompany.googleReviewLink || ""}
                  onChange={(e) =>
                    setUpdatedCompany({
                      ...updatedCompany,
                      googleReviewLink: e.target.value,
                    })
                  }
                  className="w-full p-2 mt-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Contact Number Section */}
              <div className="flex flex-col items-start">
                <label className="text-sm font-medium text-gray-700">
                  Contact Number
                </label>
                <input
                  type="text"
                  value={updatedCompany.contactNumber || ""}
                  onChange={(e) =>
                    setUpdatedCompany({
                      ...updatedCompany,
                      contactNumber: e.target.value,
                    })
                  }
                  className="w-full p-2 mt-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="flex flex-col items-start">
                <label className="text-sm font-medium text-gray-700">
                  Branch
                </label>
                <input
                  type="text"
                  value={branch || ""}
                  onChange={(e) => setBranch(e.target.value)}
                  className="w-full p-2 mt-2 border-2 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              {/* Awards Section */}
              <div className="flex flex-col items-start justify-end">
                <label className="text-sm font-medium text-gray-700">
                  Awards
                </label>
                <div className="mt-4">
                  {awards.length > 0 && (
                    <ul className="flex space-x-4">
                      {awards.map((award, index) => (
                        <li key={index}>
                          <img
                            src={award}
                            alt={`Award ${index + 1}`}
                            className="w-20 h-20 rounded-sm border-2 border-gray-300 object-contain mb-2 transition-transform duration-300 ease-in-out hover:scale-110"
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleAwardsUpload}
                  className="bg-gray-100 text-gray-700 file:border file:border-gray-400 file:rounded-lg file:px-2 file:py-1 file:hover:bg-gray-200 file:hover:border-blue-500 transition-all"
                />
              </div>

              {/* Banner Section */}
              <div className="flex flex-col items-start justify-end">
                <label className="text-sm font-medium text-gray-700">
                  Banner
                </label>
                <div className="mt-4">
                  {banner && (
                    <img
                      src={banner}
                      alt="Banner"
                      className="w-full h-40 rounded-sm border-2 border-gray-300 object-contain mb-4 transition-transform duration-300 ease-in-out hover:scale-110"
                    />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="bg-gray-100 text-gray-700 file:border file:border-gray-400 file:rounded-lg file:px-2 file:py-1 file:hover:bg-gray-200 file:hover:border-blue-500 transition-all"
                />
              </div>

              {/* Save Changes Button */}
              <div className="mt-6 text-start">
                <button
                  onClick={handleSaveChanges}
                  className="px-4 py-2 bg-blue-500 text-white font-semibold rounded-md shadow-lg hover:bg-blue-600 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminProfile;
